import { z } from 'zod'

const SkewSchema = z.enum(['bid', 'ask', 'even']).nullable()
// Query schema for endpoints that require a date and optional lookback period
export const DateRangeQuerySchema = z.object({
  date: z.string().datetime(),
  daysLookback: z.coerce.number().int().positive().optional(),
  // Coin to fetch BI data for (BI is coin-scoped)
  coingeckoId: z.string(),
})

export const CoinsListQuerySchema = z.object({
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
})

export const CoinListItemSchema = z.object({
  id: z.string(), // coingeckoId
  name: z.string(),
  symbol: z.string(),
  image: z.string().nullable(),
})

export const GetCoinsListResult = z.array(CoinListItemSchema)

// ===================== Dashboard =====================
// Schema for dashboard summary and details
export const DistributionSchema = z.object({
  spotPercentage: z.number(),
  perpPercentage: z.number(),
  leadingVenueType: z.enum(['spot', 'perp']),
})

export const GetDashboardResult = z.object({
  summary: z.object({
    appreciationPercentage: z.number().safe(),
    priceMovementDirection: z.enum(['appreciation', 'depreciation']),
    avgVolume: z.number().safe(),
    avgDepth: z.number().safe(),
    avgSpread: z.number().safe(),
    volumeDistribution: DistributionSchema,
    liquidityDistribution: DistributionSchema,
  }),
  details: z.object({
    coingeckoId: z.string(),
    price: z.number().safe().nullable(),
    priceChangePercentage: z.number().safe().nullable(),
    cexExchangeCount: z.number().safe(),
    dexExchangeCount: z.number().safe(),
    perpMarketCount: z.number().safe(),
    spotMarketCount: z.number().safe(),
    spotVolumePercentage: z.number().safe().min(0),
    perpVolumePercentage: z.number().safe().min(0),
    marketCap: z.number().safe().nullable(),
    fdv: z.number().safe().nullable(),
  }),
})

// ===================== Exchange Info =====================
// Basic exchange info schema
export const ExchangeInfoResult = z.object({
  logoUrl: z.string().nullable(),
  exchangeName: z.string(),
})

// Exchange info with a metric value (used in breakdowns)
export const ExchangeInfoWithMetricResult = z.object({
  index: z.number().int().min(0),
  exchangeName: z.string(),
  value: z.number().safe().nullable(),
})

// ===================== Marketplace Breakdown =====================
// Schema for a single marketplace breakdown item
export const MarketplaceBreakdownMetric = z.object({
  volume: z.number().safe().nullable(),
  spread: z.number().safe().nullable(),
  bid200: z.number().safe().nullable(),
  ask200: z.number().safe().nullable(),
  bidAsk200: z.number().safe().nullable(),
  ratio: z.number().safe().nullable(),
})

// Summary of marketplace breakdown (highest/lowest metrics)
export const GetMarketplaceBreakdownSummaryResult = z.object({
  cexExchangeCount: z.number().int().safe(),
  dexExchangeCount: z.number().int().safe(),
  perpMarketCount: z.number().int().safe(),
  spotMarketCount: z.number().int().safe(),
  highestVolume: ExchangeInfoResult.nullable(),
  deepestLiquidity: ExchangeInfoResult.nullable(),
  tightestSpread: ExchangeInfoResult.nullable(),
})

// Averages of marketplace breakdown
export const GetMarketplaceBreakdownAveragesResult = MarketplaceBreakdownMetric

// Full marketplace breakdown result with items and averages
export const GetMarketplaceBreakdownResult = z.object({
  data: z.array(
    MarketplaceBreakdownMetric.extend({
      exchangeLogoUrl: z.string().optional(),
      exchangeName: z.string(),
      exchangeType: z.string(),
      marketType: z.string(),
      tradingPair: z.string(),
      depthSkew: SkewSchema,
    }),
  ),
  averages: MarketplaceBreakdownMetric.extend({
    depthSkew: SkewSchema,
  }).nullable(),
})

export const OrderBookEvolutionBreakdownSchema = z.object({
  bid: z.number().safe().nullable(),
  ask: z.number().safe().nullable(),
  bidAsk: z.number().safe().nullable(),
  skew: SkewSchema,
  date: z.union([z.string().date(), z.string().datetime()]),
})

export const ChartMethodologySchema = z.enum(['spread', 'lwSpread'])
export const ChartGroupBySchema = z.enum(['exchange', 'exchangeType', 'marketType'])
export const ChartResponseSchema = z.object({
  totalGroups: z.array(z.string()),
  items: z.array(
    z.object({
      timestamp: z.number().safe(),
      groups: z.array(
        z.object({ name: z.string(), value: z.number().safe().nullable(), percentage: z.number().safe().nullable() }),
      ),
      totalValue: z.number().safe().nullable(),
    }),
  ),
})
export const PriceVsVolumeBreakdownSchema = z.object({
  date: z.union([z.string().date(), z.string().datetime()]),
  priceChange: z.number().safe().nullable(),
  volume: z.number().safe().nullable(),
  priceStart: z.number().safe().nullable(),
  priceEnd: z.number().safe().nullable(),
})

export const BookOfSpreadsBreakdownSchema = z.object({
  date: z.union([z.string().date(), z.string().datetime()]),
  avgSpreadPercentage: z.number().safe().nullable(),
  widestSpreadPercentage: z.number().safe().nullable(),
  tightestSpreadPercentage: z.number().safe().nullable(),
  spreadChangePercentage: z.number().safe().nullable(),
})

export const PriceVsVolumeQuerySchema = DateRangeQuerySchema.extend({
  marketType: z.enum(['perp', 'spot', 'all']),
})

export const VolumeChartQuerySchema = DateRangeQuerySchema.extend({
  groupBy: ChartGroupBySchema,
})

export const OrderBookEvolutionChartQuerySchema = DateRangeQuerySchema.extend({
  orderType: z.enum(['bidAsk200', 'bid200', 'ask200']),
  groupBy: ChartGroupBySchema,
})

export const BookOfSpreadsChartQuerySchema = DateRangeQuerySchema.extend({
  groupBy: ChartGroupBySchema,
  methodology: ChartMethodologySchema,
})

export const BookOfSpreadsBreakdownQuerySchema = DateRangeQuerySchema.extend({
  methodology: ChartMethodologySchema,
})

export type OrderBookEvolutionBreakdownType = z.infer<typeof OrderBookEvolutionBreakdownSchema>
export type PriceVsVolumeBreakdownType = z.infer<typeof PriceVsVolumeBreakdownSchema>
export type BookOfSpreadsBreakdownType = z.infer<typeof BookOfSpreadsBreakdownSchema>
export type ChartResponseType = z.infer<typeof ChartResponseSchema>
export type PriceVsVolumeQueryType = z.infer<typeof PriceVsVolumeQuerySchema>
export type VolumeChartQueryType = z.infer<typeof VolumeChartQuerySchema>
export type DateRangeQueryType = z.infer<typeof DateRangeQuerySchema>
export type OrderBookEvolutionChartQueryType = z.infer<typeof OrderBookEvolutionChartQuerySchema>
export type BookOfSpreadsChartQueryType = z.infer<typeof BookOfSpreadsChartQuerySchema>
export type BookOfSpreadsBreakdownQueryType = z.infer<typeof BookOfSpreadsBreakdownQuerySchema>
export type CoinsListQueryType = z.infer<typeof CoinsListQuerySchema>
export type CoinListItemType = z.infer<typeof CoinListItemSchema>
